import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import { writeBundle, writeItem } from '@/store/api/catalog/catalog.cache';
import type {
    TypeCatalogBulkSkip,
    TypeCatalogBundle,
    TypeCatalogItem,
    TypeCatalogPriceRounding,
    TypeCatalogStatus,
} from '@/types/catalog/types.catalog';
import { dropById } from '@/store/api/base/api.cache';
import type { TypeCatalogSheetOptions } from '@/types/catalog/types.catalog.sheet';
import type { TypeDocumentPdf } from '@/types/document/types.document.print';

type TypeCatalogBulkUpdateResult = {
    updated: TypeCatalogItem[];
    unchanged: string[];
    skipped: TypeCatalogBulkSkip[];
};

type TypeCatalogBulkRemoveResult = {
    removed: string[];
    skipped: TypeCatalogBulkSkip[];
};

const PAGE_SIZE = 200;

const MAX_PAGES = 40;

const walk = <T>(url: string, label: string) => walkPages<T>({
    url, fallback: `Non è stato possibile caricare ${label}`, params: { sort: 'updated' }, pageSize: PAGE_SIZE, maxPages: MAX_PAGES,
});

const toInput = (item: TypeCatalogItem, priceNote = '') => {
    const { id, code, status, priceHistory, createdAt, updatedAt, ...input } = item;
    return { ...input, priceNote };
};

const toBundleInput = (bundle: TypeCatalogBundle) => {
    const { id, code, status, createdAt, updatedAt, ...input } = bundle;
    return input;
};

export const catalogApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listCatalogItems: build.query<TypeCatalogItem[], void>({
            queryFn: () => walk<TypeCatalogItem>('/catalog/items', 'il listino'),
            providesTags: ['CatalogItem'],
        }),

        listCatalogBundles: build.query<TypeCatalogBundle[], void>({
            queryFn: () => walk<TypeCatalogBundle>('/catalog/bundles', 'i pacchetti'),
            providesTags: ['CatalogBundle'],
        }),

        createCatalogItem: build.mutation<TypeCatalogItem, { item: TypeCatalogItem; priceNote?: string; clientRef: string }>({
            query: ({ item, priceNote, clientRef }) => ({ url: '/catalog/items', method: 'post', data: { item: toInput(item, priceNote), clientRef } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeItem(dispatch, data);
                } catch {
                }
            },
        }),

        updateCatalogItem: build.mutation<TypeCatalogItem, { id: string; item: TypeCatalogItem; priceNote?: string }>({
            query: ({ id, item, priceNote }) => ({ url: `/catalog/items/${id}`, method: 'patch', data: { item: toInput(item, priceNote) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeItem(dispatch, data);
                } catch {
                }
            },
        }),

        setCatalogItemStatus: build.mutation<TypeCatalogItem, { id: string; status: TypeCatalogStatus }>({
            query: ({ id, status }) => ({ url: `/catalog/items/${id}/status`, method: 'patch', data: { status } }),
            onQueryStarted: async ({ id, status }, { dispatch, queryFulfilled }) => {
                const undo = dispatch(catalogApi.util.updateQueryData('listCatalogItems', undefined, (draft) => {
                    const entry = draft.find((item) => item.id === id);
                    if (entry) entry.status = status;
                }));
                try {
                    const { data } = await queryFulfilled;
                    writeItem(dispatch, data);
                } catch {
                    undo.undo();
                }
            },
        }),

        duplicateCatalogItem: build.mutation<TypeCatalogItem, string>({
            query: (id) => ({ url: `/catalog/items/${id}/duplicate`, method: 'post' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeItem(dispatch, data);
                } catch {
                }
            },
        }),

        removeCatalogItem: build.mutation<null, string>({
            query: (id) => ({ url: `/catalog/items/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dispatch(catalogApi.util.updateQueryData('listCatalogItems', undefined, (draft) => {
                        const index = draft.findIndex((entry) => entry.id === id);
                        if (index >= 0) draft.splice(index, 1);
                    }));
                    dispatch(catalogApi.util.updateQueryData('listCatalogBundles', undefined, (draft) => {
                        for (const bundle of draft) {
                            const index = bundle.lines.findIndex((line) => line.itemId === id);
                            if (index >= 0) bundle.lines.splice(index, 1);
                        }
                    }));
                } catch {
                }
            },
        }),

        setCatalogItemStatusMany: build.mutation<TypeCatalogBulkUpdateResult, { ids: string[]; status: TypeCatalogStatus }>({
            query: ({ ids, status }) => ({ url: '/catalog/items/status', method: 'patch', data: { ids, status } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const item of data.updated) writeItem(dispatch, item);
                } catch {
                }
            },
        }),

        setCatalogItemCategoryMany: build.mutation<TypeCatalogBulkUpdateResult, { ids: string[]; category: string }>({
            query: ({ ids, category }) => ({ url: '/catalog/items/category', method: 'patch', data: { ids, category } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const item of data.updated) writeItem(dispatch, item);
                } catch {
                }
            },
        }),

        repriceCatalogItemMany: build.mutation<TypeCatalogBulkUpdateResult, { ids: string[]; percent: number; rounding: TypeCatalogPriceRounding }>({
            query: ({ ids, percent, rounding }) => ({ url: '/catalog/items/price', method: 'patch', data: { ids, percent, rounding } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const item of data.updated) writeItem(dispatch, item);
                } catch {
                }
            },
        }),

        removeCatalogItemMany: build.mutation<TypeCatalogBulkRemoveResult, string[]>({
            query: (ids) => ({ url: '/catalog/items', method: 'delete', data: { ids } }),
            onQueryStarted: async (_ids, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    if (data.removed.length === 0) return;
                    const gone = new Set(data.removed);
                    dispatch(catalogApi.util.updateQueryData('listCatalogItems', undefined, (draft) => {
                        for (let index = draft.length - 1; index >= 0; index--) {
                            if (gone.has(draft[index].id)) draft.splice(index, 1);
                        }
                    }));
                    dispatch(catalogApi.util.updateQueryData('listCatalogBundles', undefined, (draft) => {
                        for (const bundle of draft) {
                            for (let index = bundle.lines.length - 1; index >= 0; index--) {
                                if (gone.has(bundle.lines[index].itemId)) bundle.lines.splice(index, 1);
                            }
                        }
                    }));
                } catch {
                }
            },
        }),

        createCatalogBundle: build.mutation<TypeCatalogBundle, { bundle: TypeCatalogBundle; clientRef: string }>({
            query: ({ bundle, clientRef }) => ({ url: '/catalog/bundles', method: 'post', data: { bundle: toBundleInput(bundle), clientRef } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeBundle(dispatch, data);
                } catch {
                }
            },
        }),

        updateCatalogBundle: build.mutation<TypeCatalogBundle, { id: string; bundle: TypeCatalogBundle }>({
            query: ({ id, bundle }) => ({ url: `/catalog/bundles/${id}`, method: 'patch', data: { bundle: toBundleInput(bundle) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeBundle(dispatch, data);
                } catch {
                }
            },
        }),

        setCatalogBundleStatus: build.mutation<TypeCatalogBundle, { id: string; status: TypeCatalogStatus }>({
            query: ({ id, status }) => ({ url: `/catalog/bundles/${id}/status`, method: 'patch', data: { status } }),
            onQueryStarted: async ({ id, status }, { dispatch, queryFulfilled }) => {
                const undo = dispatch(catalogApi.util.updateQueryData('listCatalogBundles', undefined, (draft) => {
                    const entry = draft.find((bundle) => bundle.id === id);
                    if (entry) entry.status = status;
                }));
                try {
                    const { data } = await queryFulfilled;
                    writeBundle(dispatch, data);
                } catch {
                    undo.undo();
                }
            },
        }),

        duplicateCatalogBundle: build.mutation<TypeCatalogBundle, string>({
            query: (id) => ({ url: `/catalog/bundles/${id}/duplicate`, method: 'post' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeBundle(dispatch, data);
                } catch {
                }
            },
        }),

        removeCatalogBundle: build.mutation<null, string>({
            query: (id) => ({ url: `/catalog/bundles/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dispatch(catalogApi.util.updateQueryData('listCatalogBundles', undefined, (draft) => {
                        dropById(draft, id);
                    }));
                } catch {
                }
            },
        }),
        readCatalogSheetPdf: build.mutation<TypeDocumentPdf, TypeCatalogSheetOptions>({
            query: (options) => ({ url: '/catalog/sheet/pdf', method: 'post', data: { options: { ...options, customerId: options.customerId ?? '', validUntil: options.validUntil ?? '', highlightBundle: options.highlightBundle ?? '' } } }),
        }),
    }),
});

export const {
    useListCatalogItemsQuery,
    useListCatalogBundlesQuery,
    useCreateCatalogItemMutation,
    useUpdateCatalogItemMutation,
    useSetCatalogItemStatusMutation,
    useDuplicateCatalogItemMutation,
    useRemoveCatalogItemMutation,
    useSetCatalogItemStatusManyMutation,
    useSetCatalogItemCategoryManyMutation,
    useRepriceCatalogItemManyMutation,
    useRemoveCatalogItemManyMutation,
    useCreateCatalogBundleMutation,
    useUpdateCatalogBundleMutation,
    useSetCatalogBundleStatusMutation,
    useDuplicateCatalogBundleMutation,
    useRemoveCatalogBundleMutation,
    useReadCatalogSheetPdfMutation,
} = catalogApi;

export default catalogApi;
